import classNames from "classnames";
import { differenceInDays, startOfMonth, subMonths } from "date-fns";
import { ErrorMessage, useField } from "formik";
import { useCallback } from "react";
import { DateRangePickerField } from "../../atoms/DateRangePickerField";
import { useMultiSectionBatchesDateRangePicker } from "./useMultiSectionBatchesDateRangePicker";

type Props = {
  allowedDateRangeDays?: number;
};

export const MultiSectionBatchesDownloadDateRangePicker = ({
  allowedDateRangeDays,
}: Props) => {
  const [field, meta, helpers] = useField("dateRange");
  const { value: dateRange } = field;
  const { touched, error } = meta;
  const { setValue, setTouched } = helpers;
  const { disabled } = useMultiSectionBatchesDateRangePicker(
    dateRange,
    allowedDateRangeDays,
  );

  // カレンダーを閉じたタイミングでバリデーションを実行
  // 開始日が入力されていて、終了日が入力されていない状態の場合のみ touched にする
  const handleCalendarClose = useCallback(
    (newDateRange: typeof dateRange) => {
      setTouched(
        newDateRange.startDate !== undefined &&
          newDateRange.endDate === undefined,
      );
    },
    [setTouched],
  );

  // 日付範囲が変更されたタイミングでエラー表示を制御
  // 開始日と終了日が両方入力されている場合は touched を false にしてエラー表示を消す
  const handleChangeDateRange = useCallback(
    (dates: typeof dateRange) => {
      setValue(dates);
      if (dates.startDate !== undefined && dates.endDate !== undefined) {
        setTouched(false);
      }
    },
    [setValue, setTouched],
  );

  const hasError = !!(touched && error);
  return (
    <div className="flex flex-col gap-8 rounded-lg bg-gray-100 px-10 max-md:px-4 py-8">
      <div>
        <p>2. 出力したい期間を選択してください</p>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <div
          className="w-[30rem]"
          onClick={() => {
            if (dateRange.startDate && dateRange.endDate) {
              setValue({});
            }
          }}
        >
          <DateRangePickerField
            dateRange={dateRange}
            onChangeDateRange={handleChangeDateRange}
            placeholder="出力期間を選択"
            disabled={disabled}
            className={classNames({
              "bg-white": !hasError,
              "bg-darkred-100": hasError,
            })}
            fromDate={new Date(2024, 3, 1)} // NOTE: 2024/4/1 以降の日付を選択可能にする
            hasError={hasError}
            onCalendarClose={handleCalendarClose}
            defaultMonthWhenEmpty={startOfMonth(subMonths(new Date(), 1))}
          />
          <ErrorMessage
            name="dateRange"
            component="div"
            className="my-2 text-red-400"
          />
        </div>
        {dateRange.startDate !== undefined &&
          dateRange.endDate !== undefined && (
            <p className="font-bold">
              の{differenceInDays(dateRange.endDate, dateRange.startDate) + 1}
              日間
            </p>
          )}
      </div>
    </div>
  );
};
